import FileIcon from "@uui/File07";

import type { SxProps } from "@mui/material";

import ExpandableVideo, {
  ExpandableVideoProps,
} from "@app/analytics/components/ExpandableVideo";
import PlayClip from "@app/analytics/components/PlayClip";
import useRecordingStatus from "@app/analytics/hooks/recordings/useRecordingStatus";
import { SecondaryTextWithIcon } from "@app/analytics/Pulse/components/styles";
import type { PulseTopicClip } from "@app/analytics/Pulse/types";
import { getClip } from "@app/analytics/Pulse/utils";
import * as Events from "@app/analytics/tracking/events";
import ROUTES from "@app/common/routes";
import { ELLIPSIS_SX } from "@app/common/styles";
import type {
  UnauthorizedVideoRecording,
  VideoRecording,
} from "@app/common/types";
import { withQueryParamsUrl } from "@app/common/utils";

import { Column } from "@rui/Stack";
import TooltipWrapper from "@rui/TooltipWrapper";

import {
  ClipDetails,
  ClipWrapper,
  ClipContent,
  Thumbnail,
  ThumbnailOverlay,
} from "./styles";

export interface ClipProps {
  clip: PulseTopicClip;
  forcePause?: ExpandableVideoProps["forcePause"];
  isLoading?: boolean;
  onPlay?: ExpandableVideoProps["onPlay"];
  recordingInfo?: VideoRecording | UnauthorizedVideoRecording;
  sx?: SxProps;
}

const Clip = ({
  clip,
  isLoading = false,
  recordingInfo,
  sx,
  ...videoProps
}: ClipProps) => {
  const { meetingTitle, sessionId, thumbnailUrl, text } = clip;

  const videoClip = getClip(clip, recordingInfo);

  const { status, message } = useRecordingStatus({ recordingInfo, isLoading });

  const videoUnavailable =
    videoClip == null || (status !== "available" && status !== "unknown");

  const handleTitleClick = () => {
    Events.clickClip({ context: "to_report" });
  };

  return (
    <Column sx={sx}>
      <ClipWrapper>
        <Column sx={{ alignSelf: "flex-start" }}>
          {videoUnavailable ? (
            <TooltipWrapper show={!isLoading} title={message}>
              <Thumbnail
                sx={{
                  backgroundImage: thumbnailUrl
                    ? `url("${thumbnailUrl}")`
                    : undefined,
                }}
              >
                {!isLoading && (
                  <ThumbnailOverlay
                    icon={recordingInfo == null ? "no_video" : "lock"}
                  />
                )}
              </Thumbnail>
            </TooltipWrapper>
          ) : (
            <ExpandableVideo
              {...videoProps}
              forcePauseOnMinimize
              recordingInfo={recordingInfo as VideoRecording}
              clip={videoClip}
              containerSx={{
                width: "160px",
                height: "96px",
                borderRadius: 0,
              }}
              thumbnailUrl={thumbnailUrl}
              renderMinimizedOverlay={(props) => (
                <PlayClip
                  sx={{ position: "absolute", bottom: "3px", right: "9px" }}
                  {...props}
                />
              )}
            />
          )}
        </Column>
        <ClipDetails spacing={0.5}>
          <ClipContent>{text}</ClipContent>
          <SecondaryTextWithIcon
            Icon={FileIcon}
            color="shades.neutral.400"
            href={withQueryParamsUrl(ROUTES.meetingReportById(sessionId), {
              t: clip.timestamp.value,
            })}
            onClick={handleTitleClick}
            title={meetingTitle}
            sx={{
              fontSize: "10px",
              maxWidth: "100%",
              fontWeight: 400,
              ...ELLIPSIS_SX,
            }}
          >
            {meetingTitle}
          </SecondaryTextWithIcon>
        </ClipDetails>
      </ClipWrapper>
    </Column>
  );
};

export default Clip;
